// Online orders, shown in place of the style grid: orders from the online
// store that this store fulfils. Accept for pickup or to ship, pack, bill,
// then hand over against the customer's pickup code or ship with a tracking
// number. Online only — orders are shared by every till in the store. A
// handed-over or shipped order can be brought back to the store and returned
// like any bill (store credit or exchange only — see FashionReturnView).
// Lines charged at a different price from the store's own (price list plus
// markdown) are flagged so one price holds across channels.
// Store bills shipped home (channel "In store") are listed here too, with the
// delivery address; they have no channel to tell and no Cancel (return them
// from the bill), and their return is an ordinary store return.
import React, { useState } from "react";
import dayjs from "dayjs";
import useFashionOnlineOrders from "./hooks/useFashionOnlineOrders";
import { startOrderReturn } from "./FashionBillsPanel";
import { canCancelOnline, CANCEL_REASONS, cancelledOnChannel, channelBehind, itemsCount, markOnlineReturn, MODE_LABEL, priceGaps, STATUS_LABEL, validatePickupCode, validateShipment } from "../../../utils/fashion/onlineOrders";
import { formatMoney } from "../../../utils/fashion/money";
import { shipToLine } from "../../../utils/fashion/shipTo";
import EmptyState from "./EmptyState";

const showTime = (d) => (d && dayjs(d).isValid() ? dayjs(d).format("DD MMM, HH:mm") : d || "");

function HandOverForm({ order, run }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const { code: c, error } = validatePickupCode(code);
    if (error) return setErr(error);
    setErr("");
    run(order, "DLV", c);
  };
  return (
    <form className="fp-row" onSubmit={submit} aria-label={`Hand over ${order.documentNo}`}>
      <input className="fp-input fp-num fp-field-sm" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Pickup code" aria-label="Pickup code" autoComplete="off" />
      <button type="submit" className="fp-btn-ghost fp-btn-sm">
        Hand over
      </button>
      {err && (
        <span className="fp-line-msg is-err" role="alert">
          {err}
        </span>
      )}
    </form>
  );
}

function ShipForm({ order, run }) {
  const [courier, setCourier] = useState(order.courier || "");
  const [awb, setAwb] = useState(order.awbNo || "");
  const [err, setErr] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const res = validateShipment(courier, awb);
    if (res.error) return setErr(res.error);
    setErr("");
    run(order, "SHP", res);
  };
  return (
    <form className="fp-row" onSubmit={submit} aria-label={`Ship ${order.documentNo}`}>
      <input className="fp-input fp-field-sm" value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="Courier" aria-label="Courier" autoComplete="off" />
      <input className="fp-input fp-num fp-field-sm" value={awb} onChange={(e) => setAwb(e.target.value)} placeholder="AWB no." aria-label="AWB no." autoComplete="off" />
      <button type="submit" className="fp-btn-ghost fp-btn-sm">
        Ship
      </button>
      {err && (
        <span className="fp-line-msg is-err" role="alert">
          {err}
        </span>
      )}
    </form>
  );
}

// Tabs as the store works them. New, Picking and Ready are the open orders
// (one OPEN fetch) split by status here; Done and Cancelled are fetched alone.
export const ONLINE_TABS = [
  { key: "NEW", label: "New", server: "OPEN", only: "NEW" },
  { key: "ACC", label: "Picking", server: "OPEN", only: "ACC" },
  { key: "RDY", label: "Ready", server: "OPEN", only: "RDY" },
  { key: "DONE", label: "Done", server: "DONE" },
  { key: "CAN", label: "Cancelled", server: "CAN" },
];
const OPEN_STEPS = ["NEW", "ACC", "RDY"];
// Store-side names for the open steps; the rest keep the shared labels.
const CHIP_LABEL = { ...STATUS_LABEL, ACC: "Picking", RDY: "Ready" };

// Until the cashier picks a tab, show the first open step that has orders.
export function autoOnlineTab(openRows) {
  const hit = ONLINE_TABS.find((t) => t.only && openRows.some((o) => o.status === t.only));
  return hit ? hit.key : "NEW";
}

export default function FashionOnlinePanel({ tillData, isOnline, storePriceFor, returnProps, onReturnStarted, onClose, backLabel = "Back to styles" }) {
  const [picked, setPicked] = useState(null);
  const [server, setServer] = useState("OPEN");
  const [term, setTerm] = useState("");
  const [msg, setMsg] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(null);
  const [reasonCode, setReasonCode] = useState(CANCEL_REASONS[0].value);
  const { rows, status, pending, online, reload, accept, ready, handOver, ship, cancel, bill, resendChannel } = useFashionOnlineOrders(tillData, isOnline, server, term.trim());
  const tabKey = picked || (server === "OPEN" ? autoOnlineTab(rows) : server);
  const tab = ONLINE_TABS.find((t) => t.key === tabKey) || ONLINE_TABS[0];
  // A row outside the open steps (shouldn't come back for OPEN) is never hidden.
  const shown = tab.only && server === "OPEN" ? rows.filter((o) => o.status === tab.only || !OPEN_STEPS.includes(o.status)) : rows;
  const countOf = (t) => (t.only && server === "OPEN" ? rows.filter((o) => o.status === t.only).length : null);
  const pickTab = (t) => {
    setPicked(t.key);
    setServer(t.server);
  };

  const run = async (order, action, arg) => {
    if (busy) return;
    if (action === "CAN" && confirming !== order.id) {
      setConfirming(order.id);
      setReasonCode(CANCEL_REASONS[0].value);
      return;
    }
    setConfirming(null);
    setBusy(order.id);
    setMsg(null);
    const doc = order.documentNo || "Order";
    try {
      let res;
      if (action === "PICKUP" || action === "SHIP") res = await accept(order.id, action);
      else if (action === "RDY") res = await ready(order.id, order.storeSale);
      else if (action === "BILL") res = await bill(order.id);
      else if (action === "DLV") res = await handOver(order.id, arg);
      else if (action === "SHP") res = await ship(order.id, arg.courier, arg.awbNo, order.storeSale);
      else if (action === "CAN" && canCancelOnline(order)) {
        const lost = cancelledOnChannel(order);
        const label = CANCEL_REASONS.find((r) => r.value === reasonCode)?.label;
        res = await cancel(order.id, lost ? "Cancelled on the online store" : `Cancelled by store: ${label || "Other"}`, lost ? null : reasonCode, lost);
      }
      else if (action === "CHANNEL") res = await resendChannel(order.id, arg);
      else if (action === "RETURN") {
        // A store bill shipped home was paid at the till: an ordinary return.
        if (!order.storeSale) markOnlineReturn(order.id);
        const started = await startOrderReturn({ documentno: order.documentNo, sOrderID: order.id }, returnProps || {});
        if (started?.ok) {
          onReturnStarted?.();
          return;
        }
        res = { ok: false, message: started?.error || "Couldn't open this order for return. Try again." };
      }
      if (!res?.ok) setMsg({ text: res?.message || "Couldn't update the order. Try again.", err: true });
      else {
        // Let the list follow the work: an explicit New/Picking pick re-settles on the next open step with orders.
        if (["PICKUP", "SHIP", "RDY"].includes(action)) setPicked((k) => (k === "NEW" || k === "ACC" ? null : k));
        setMsg({ text: [res.message ? `${doc}: ${res.message}` : `${doc} updated.`, res.warn].filter(Boolean).join(" "), err: Boolean(res.warn) });
      }
    } catch {
      setMsg({ text: "Couldn't update the order. Check the connection and try again.", err: true });
    } finally {
      setBusy(null);
    }
  };

  const cancelButton = (order) => {
    if (!canCancelOnline(order)) return null;
    const off = busy === order.id;
    if (confirming !== order.id) {
      return (
        <button type="button" className="fp-btn-ghost fp-btn-sm" disabled={off} onClick={() => run(order, "CAN")}>
          Cancel
        </button>
      );
    }
    return (
      <>
        {!cancelledOnChannel(order) && (
          <select className="fp-input fp-field-sm" value={reasonCode} onChange={(e) => setReasonCode(e.target.value)} aria-label="Reason for cancelling">
            {CANCEL_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        )}
        <button type="button" className="fp-btn-ghost fp-btn-sm" disabled={off} onClick={() => run(order, "CAN")}>
          Confirm cancel
        </button>
        <button type="button" className="fp-btn-ghost fp-btn-sm" disabled={off} onClick={() => setConfirming(null)}>
          Keep
        </button>
      </>
    );
  };

  const actions = (order) => {
    const off = busy === order.id;
    const lost = cancelledOnChannel(order);
    if (order.status === "NEW") {
      if (lost) return <span className="fp-muted">Cancelled online</span>;
      return (
        <>
          <button type="button" className="fp-btn-ghost fp-btn-sm" disabled={off} onClick={() => run(order, "PICKUP")}>
            Accept for pickup
          </button>
          <button type="button" className="fp-btn-ghost fp-btn-sm" disabled={off} onClick={() => run(order, "SHIP")}>
            Accept to ship
          </button>
        </>
      );
    }
    if (order.status === "ACC") {
      return (
        <>
          {!lost && (
            <button type="button" className="fp-btn-ghost fp-btn-sm" disabled={off} onClick={() => run(order, "RDY")}>
              Mark packed
            </button>
          )}
          {cancelButton(order)}
        </>
      );
    }
    if (order.status === "RDY") {
      if (!order.billed) {
        return (
          <>
            {!lost && (
              <button type="button" className="fp-btn-ghost fp-btn-sm" disabled={off} onClick={() => run(order, "BILL")}>
                Bill
              </button>
            )}
            {cancelButton(order)}
          </>
        );
      }
      if (lost) return <span className="fp-muted">Billed — return it from the bill</span>;
      return order.mode === "SHIP" ? <ShipForm order={order} run={run} /> : <HandOverForm order={order} run={run} />;
    }
    if ((order.status === "DLV" || order.status === "SHP") && order.billed && order.documentNo) {
      return (
        <button type="button" className="fp-btn-ghost fp-btn-sm" disabled={off} onClick={() => run(order, "RETURN")}>
          Return in store
        </button>
      );
    }
    return null;
  };

  return (
    <section className="fp-panel" aria-label="Online orders">
      <div className="fp-panel-head">
        <h2 className="fp-panel-title">Online orders</h2>
        <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={onClose}>
          {backLabel}
        </button>
      </div>
      {!online ? (
        <EmptyState icon="offline" title="You're offline" hint="Online orders need a connection. Reconnect the till and try again." />
      ) : (
        <>
          {msg && (
            <p className={`fp-line-msg${msg.err ? " is-err" : ""}`} role={msg.err ? "alert" : "status"}>
              {msg.text}
            </p>
          )}
          <div className="fp-wp-seg is-scroll fp-panel-seg" role="group" aria-label="Show">
            {ONLINE_TABS.map((t) => {
              const n = countOf(t);
              return (
                <button key={t.key} type="button" className={tab.key === t.key ? "is-on" : ""} aria-pressed={tab.key === t.key} onClick={() => pickTab(t)}>
                  {t.label}
                  {n ? ` · ${n}` : ""}
                </button>
              );
            })}
          </div>
          <p className="fp-muted fp-onl-info">Orders are routed to this store when it has every piece in stock. Accepting reserves the pieces so no till can sell them.</p>
          <div className="fp-row">
            <input className="fp-input fp-panel-search" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search order no., name or mobile" aria-label="Search online orders" />
            <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => reload()} disabled={status === "loading"}>
              Refresh
            </button>
          </div>
          {status === "error" && (
            <p className="fp-line-msg is-err" role="alert">
              Couldn't load online orders. The list below may be out of date.
            </p>
          )}
          <ul className="fp-panel-list" aria-busy={status === "loading" ? "true" : "false"}>
            {shown.length === 0 && (
              status === "loading" || status === "idle" ? (
                <li className="fp-empty">Loading online orders…</li>
              ) : term.trim() ? (
                <EmptyState as="li" icon="search" title={`No online order matches “${term.trim()}”.`} hint="Check the spelling, or try another search." />
              ) : (
                <EmptyState as="li" title={`No ${tab.label.toLowerCase()} orders.`} hint="Online orders for this store show here as they come in." />
              )
            )}
            {shown.map((order) => {
              const lost = cancelledOnChannel(order);
              const behind = lost ? null : pending[order.id]?.code || channelBehind(order);
              const gaps = order.status === "CAN" || order.billed ? [] : priceGaps(order, storePriceFor);
              return (
                <li key={order.id} className="fp-panel-row">
                  <span className="fp-panel-main">
                    <span className="fp-panel-doc">
                      <span className="fp-num">{order.externalId || order.documentNo}</span>
                      {order.channel ? ` · ${order.channel}` : ""} <span className={`fp-chip fp-chip-sm fp-onl-${order.status}`}>{CHIP_LABEL[order.status] || order.status}</span>
                      {order.mode && <span className="fp-chip fp-chip-sm">{MODE_LABEL[order.mode] || order.mode}</span>}
                      {order.billed && <span className="fp-chip fp-chip-sm">Billed</span>}
                      {lost && <span className="fp-chip fp-chip-sm fp-alt-late">Cancelled online</span>}
                    </span>
                    <span className="fp-muted">
                      {[order.customerName, order.mobileNo].filter(Boolean).join(" · ")}
                      {` · ${itemsCount(order)} ${itemsCount(order) === 1 ? "piece" : "pieces"} · ${formatMoney(order.total, tillData)}`}
                      {order.ordered ? ` · ordered ${showTime(order.ordered)}` : ""}
                      {order.awbNo ? ` · ${order.courier} ${order.awbNo}` : ""}
                      {order.mode === "PICKUP" && order.status === "RDY" && order.otpTriesLeft !== null ? ` · ${order.otpTriesLeft} code tries left` : ""}
                    </span>
                    <span className="fp-muted">{order.lines.map((l) => `${l.qty} × ${l.name}`).join(", ")}</span>
                    {order.storeSale && (
                      <span className="fp-muted">{order.shipTo ? `Ship to: ${shipToLine(order.shipTo)}` : "Ship to: address not readable — check the bill before shipping."}</span>
                    )}
                    {gaps.length > 0 && (
                      <span className="fp-line-msg is-err">
                        Price differs from the store:{" "}
                        {gaps.map((g) => `${g.name} online ${formatMoney(g.online, tillData)}, store ${formatMoney(g.store, tillData)}`).join("; ")}. Check the price before packing, or cancel as “Price or total wrong”.
                      </span>
                    )}
                    {lost && order.status !== "NEW" && <span className="fp-line-msg is-err">The customer cancelled this online. Put the pieces back and cancel it here.</span>}
                  </span>
                  <span className="fp-panel-acts">
                    {actions(order)}
                    {behind && (
                      <button type="button" className="fp-btn-ghost fp-btn-sm" disabled={busy === order.id} onClick={() => run(order, "CHANNEL", behind)}>
                        Update channel
                      </button>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
